import { Component, Input, Output, EventEmitter, HostListener, ElementRef, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'multiselect',
  templateUrl: './multiselect.component.html',
  styleUrls: ['./multiselect.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiselectComponent),
      multi: true
    }
  ]
})
export class MultiselectComponent implements ControlValueAccessor {

  // String mode (rétro-compatible) : la valeur sélectionnée EST le libellé.
  @Input() list: string[] = [];
  // Object mode (optionnel) : sélection par `id`, affichage par `label`. Prioritaire sur `list`.
  @Input() items: { id: any; label: string }[] = null;
  @Input() placeholder: string = 'Sélectionner...';
  @Input() required: boolean = false;
  @Output() selectionChange = new EventEmitter<any[]>();

  // Valeurs sélectionnées : libellés (string mode) ou ids (object mode).
  selectedValues: any[] = [];
  isDropdownOpen = false;
  isDisabled = false;

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(private elementRef: ElementRef) { }

  // Liste normalisée { value, label } selon le mode (object si `items` fourni, sinon string).
  get options(): { value: any; label: string }[] {
    if (this.items) {
      return this.items.map(i => ({ value: i.id, label: i.label }));
    }
    return (this.list || []).map(s => ({ value: s, label: s }));
  }

  // Libellés des valeurs sélectionnées, pour l'affichage replié.
  get selectedLabels(): string {
    return this.options
      .filter(o => this.isSelected(o.value))
      .map(o => o.label)
      .join(', ');
  }

  // Ferme le dropdown si on clique en dehors du composant
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen = false;
    }
  }

  writeValue(value: any[]): void {
    this.selectedValues = value || [];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  toggleSelection(value: any) {
    const index = this.selectedValues.indexOf(value);

    if (index > -1) {
      this.selectedValues.splice(index, 1);
    } else {
      this.selectedValues.push(value);
    }

    // Émet la nouvelle liste de valeurs sélectionnées
    this.onChange(this.selectedValues);
    this.onTouched();
    this.selectionChange.emit(this.selectedValues);
  }

  isSelected(value: any): boolean {
    return this.selectedValues.indexOf(value) > -1;
  }
}
